import React, { useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";

/**
 * Converts API string numbers like:
 * "1,890.00"
 * "2230.20"
 * "18%"
 *
 * into valid numbers.
 */
const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const cleanedValue = String(value)
    .replace(/,/g, "")
    .replace(/%/g, "")
    .trim();

  const number = Number(cleanedValue);

  return Number.isNaN(number) ? 0 : number;
};

/**
 * Currency formatter
 */
const formatAmount = (value) => {
  return toNumber(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Get all matched items from:
 *
 * matched_results
 *   -> filter_results
 *      -> matched_items
 */
const getMatchedItems = (record) => {
  const filterResults = record?.matched_results?.filter_results || [];

  return filterResults.flatMap((filter) => {
    const matchedItems = filter?.matched_items || [];

    return matchedItems.map((item, index) => ({
      ...item,

      id:
        item?.item_code ||
        `${filter?.filter_id || "FILTER"}-${item?.row_number || index}`,

      filter_id: filter?.filter_id,
      filter_name: filter?.name,
      source_requirement: filter?.source_requirement,

      item_code: item?.item_code || "-",
      item_name: item?.item_name || "-",
      description: item?.description || "-",

      quantity: toNumber(item?.quantity),
      uom: item?.uom || "-",

      // DIRECTLY FROM API
      unit_price: toNumber(item?.unit_price),

      tax: item?.tax || "-",

      // DIRECTLY FROM API
      total_price: toNumber(item?.total_price),

      currency: item?.currency || "INR",
      row_number: item?.row_number,
    }));
  });
};

/**
 * Creates tax text from company_details.tax
 *
 * Example:
 * GST 18%
 *
 * OR
 *
 * CGST 9% | SGST 9%
 */
const getTaxDisplay = (tax = {}) => {
  const taxParts = [];

  if (Array.isArray(tax?.gst) && tax.gst.length > 0) {
    taxParts.push(`GST ${tax.gst.join(", ")}`);
  }

  if (Array.isArray(tax?.cgst) && tax.cgst.length > 0) {
    taxParts.push(`CGST ${tax.cgst.join(", ")}`);
  }

  if (Array.isArray(tax?.sgst) && tax.sgst.length > 0) {
    taxParts.push(`SGST ${tax.sgst.join(", ")}`);
  }

  if (Array.isArray(tax?.igst) && tax.igst.length > 0) {
    taxParts.push(`IGST ${tax.igst.join(", ")}`);
  }

  return taxParts.length > 0 ? taxParts.join(" | ") : "-";
};

const PriceListQuotationView = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  /**
   * Expected selectedPriceList:
   *
   * {
   *   created_date: "...",
   *   company_details: {...},
   *   matched_results: {...}
   * }
   */
  const selectedPriceList = state?.selectedPriceList || null;

  /**
   * Convert filter_results -> matched_items into
   * one table array.
   */
  const lineItems = useMemo(() => {
    return getMatchedItems(selectedPriceList);
  }, [selectedPriceList]);

  /**
   * COMPANY DETAILS
   */
  const companyDetails = selectedPriceList?.company_details || {};

  const supplierName = companyDetails?.company_name || "-";

  const supplierAddress = companyDetails?.location || "-";

  const supplierGst = companyDetails?.gst_number || "-";

  const supplierPan = companyDetails?.pan_number || "-";

  const warranty = companyDetails?.warranty || "-";

  /**
   * TAX DETAILS
   */
  const companyTax = companyDetails?.tax || {};

  const taxDisplay = getTaxDisplay(companyTax);

  /**
   * IMPORTANT:
   * Do NOT calculate subtotal again.
   *
   * Directly map:
   * matched_results.total_price_without_tax
   */
  const subtotal =
    selectedPriceList?.matched_results?.total_price_without_tax || "0.00";

  /**
   * IMPORTANT:
   * Directly map API grand total.
   */
  const grandTotal =
    selectedPriceList?.matched_results?.grand_total || "0.00";

  /**
   * Currency from first item.
   */
  const currency =
    lineItems.find((item) => item?.currency)?.currency || "INR";

  /**
   * Created date from API
   */
  const quotationDate = useMemo(() => {
    const dateValue = selectedPriceList?.created_date;

    if (!dateValue) {
      return "-";
    }

    // API:
    // 2026-08-31 12:22:29
    //
    // Replace space so browser can parse it better.
    const parsedDate = new Date(dateValue.replace(" ", "T"));

    if (Number.isNaN(parsedDate.getTime())) {
      return dateValue;
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, [selectedPriceList?.created_date]);

  return (
    <Box sx={{ p: 2 }}>
      {/* ================= HEADER ================= */}

      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <HeaderTitle titleText="Quotation Details" />

        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
          }}
          onClick={() => navigate("/Buyer_dashboard/price_list")}
        >
          Back
        </Button>
      </Box>

      {/* ================= COMPANY DETAILS ================= */}

      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",

          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
          },

          gap: 1.5,
        }}
      >
        {/* Company Name */}

        <Typography
          sx={{
            fontFamily: "GilroySemiBold",
            fontSize: "15px",
          }}
        >
          Company Name:{" "}
          <Box
            component="span"
            sx={{
              fontFamily: "GilroyMedium",
            }}
          >
            {supplierName}
          </Box>
        </Typography>

        {/* Location */}

        <Typography
          sx={{
            fontFamily: "GilroySemiBold",
            fontSize: "15px",
          }}
        >
          Location:{" "}
          <Box
            component="span"
            sx={{
              fontFamily: "GilroyMedium",
            }}
          >
            {supplierAddress}
          </Box>
        </Typography>

        {/* GST */}

        <Typography
          sx={{
            fontFamily: "GilroySemiBold",
            fontSize: "15px",
          }}
        >
          GSTIN:{" "}
          <Box
            component="span"
            sx={{
              fontFamily: "GilroyMedium",
            }}
          >
            {supplierGst}
          </Box>
        </Typography>

        {/* PAN */}

        <Typography
          sx={{
            fontFamily: "GilroySemiBold",
            fontSize: "15px",
          }}
        >
          PAN:{" "}
          <Box
            component="span"
            sx={{
              fontFamily: "GilroyMedium",
            }}
          >
            {supplierPan}
          </Box>
        </Typography>

        {/* Date */}

        <Typography
          sx={{
            fontFamily: "GilroySemiBold",
            fontSize: "15px",
          }}
        >
          Date:{" "}
          <Box
            component="span"
            sx={{
              fontFamily: "GilroyMedium",
            }}
          >
            {quotationDate}
          </Box>
        </Typography>
      </Box>

      {/* ================= ITEM TABLE ================= */}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {[
                "Sl No",
                "Part No",
                "Item Name",
                "Description",
                "Quantity",
                "UOM",
                "Unit Price",
                "Tax",
                "Total Amount",
              ].map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: 600,
                    fontFamily: "GilroySemiBold",
                    backgroundColor: "#e0e9ee",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {lineItems.length > 0 ? (
              lineItems.map((item, index) => (
                <TableRow key={`${item.id}-${index}`}>
                  {/* Sl No */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                    }}
                  >
                    {index + 1}
                  </TableCell>

                  {/* Part No */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                    }}
                  >
                    {item.item_code}
                  </TableCell>

                  {/* Item Name */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                    }}
                  >
                    {item.item_name}
                  </TableCell>

                  {/* Description */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                    }}
                  >
                    {item.description}
                  </TableCell>

                  {/* Quantity */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                    }}
                  >
                    {item.quantity}
                  </TableCell>

                  {/* UOM */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                    }}
                  >
                    {item.uom}
                  </TableCell>

                  {/* UNIT PRICE - DIRECT FROM API */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {currency} {formatAmount(item.unit_price)}
                  </TableCell>

                  {/* TAX */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                    }}
                  >
                    {item.tax}
                  </TableCell>

                  {/* TOTAL PRICE - DIRECT FROM API */}

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroySemiBold",
                      color: "#075b97",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {currency} {formatAmount(item.total_price)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{
                    py: 4,
                    fontFamily: "GilroyMedium",
                    color: "#64748b",
                  }}
                >
                  No matched items found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ===================================================
          WARRANTY LEFT + TOTAL SUMMARY RIGHT
         =================================================== */}

      <Box
        sx={{
          mt: 3,

          display: "flex",

          flexDirection: {
            xs: "column",
            md: "row",
          },

          justifyContent: "space-between",

          alignItems: {
            xs: "stretch",
            md: "flex-start",
          },

          gap: 2,
        }}
      >
        {/* ================= WARRANTY - LEFT SIDE ================= */}

        <Box
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",

            maxWidth: {
              xs: "100%",
              md: "55%",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: "GilroySemiBold",
              fontSize: "15px",
              mb: 0.8,
              color: "#1e293b",
            }}
          >
            Warranty
          </Typography>

          <Typography
            sx={{
              fontFamily: "GilroyMedium",
              fontSize: "14px",
              color: "#475569",
              lineHeight: 1.6,
            }}
          >
            {warranty}
          </Typography>
        </Box>

        {/* ================= TOTAL SUMMARY - RIGHT SIDE ================= */}

        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: "#f0f7fa",
            border: "1px solid #cbd5e1",

            display: "flex",
            flexDirection: "column",
            gap: 1.2,

            width: {
              xs: "100%",
              md: "400px",
            },

            boxSizing: "border-box",
          }}
        >
          {/* SUBTOTAL WITHOUT TAX */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "GilroyMedium",
                fontSize: "14px",
              }}
            >
              Subtotal (Excl. Tax)
            </Typography>

            <Typography
              sx={{
                fontFamily: "GilroySemiBold",
                fontSize: "14px",
              }}
            >
              {currency} {formatAmount(subtotal)}
            </Typography>
          </Box>

          {/* TAX */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "GilroyMedium",
                fontSize: "14px",
              }}
            >
              Tax
            </Typography>

            <Typography
              sx={{
                fontFamily: "GilroySemiBold",
                fontSize: "14px",
              }}
            >
              {taxDisplay}
            </Typography>
          </Box>

          {/* DIVIDER */}

          <Box
            sx={{
              borderTop: "1px solid #cbd5e1",
              my: 0.5,
            }}
          />

          {/* GRAND TOTAL */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "GilroySemiBold",
                fontSize: "16px",
                color: "#075b97",
              }}
            >
              Grand Total
            </Typography>

            <Typography
              sx={{
                fontFamily: "GilroySemiBold",
                fontSize: "18px",
                color: "#075b97",
                whiteSpace: "nowrap",
              }}
            >
              {currency} {formatAmount(grandTotal)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PriceListQuotationView;